import { cacheKeys, useWorkspaceAsset } from '@gripp/shared-logic';
import { RoutineButtons, RoutineExecutionProps } from '@gripp/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { useParams } from 'react-router-dom';
import { RoutinesAssignedListModal } from '../../routines/components/routinesAssignedListModal';
import { ViewAllSectionHeader } from './viewAllSectionHeader';

const INITIAL_ROUTINES_TO_SHOW = 3;

export const AssetRoutineFold: React.FC<{ assetId: string }> = ({
  assetId,
}) => {
  const { workspaceAsset, invalidateQuery: invalidateWorkspaceAsset } =
    useWorkspaceAsset(assetId);
  const [routinesModalOpened, setRoutinesModalOpened] = useState(false);
  const [initialRoutine, setInitialRoutine] = useState<
    RoutineExecutionProps | undefined
  >();
  const queryClient = useQueryClient();
  const invalidateIssues = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: cacheKeys('issue').all });
  }, [queryClient]);
  const { t } = useTranslation();

  const onRoutineCompleted = useCallback(async () => {
    setRoutinesModalOpened(false);
    setInitialRoutine(undefined);
    await invalidateWorkspaceAsset();
    await invalidateIssues();
  }, [invalidateIssues, invalidateWorkspaceAsset]);

  const onRoutineSelected = useCallback((routine: RoutineExecutionProps) => {
    setInitialRoutine(routine);
    setRoutinesModalOpened(true);
  }, []);
  const { issueId } = useParams();

  if (!workspaceAsset || issueId) return null;

  return (
    <>
      <View style={styles.containerHeader}>
        <ViewAllSectionHeader
          title={t('assets.routines')}
          onPress={() => setRoutinesModalOpened(true)}
        />
        <RoutineButtons
          workspaceAsset={workspaceAsset}
          initialRoutinesToShow={INITIAL_ROUTINES_TO_SHOW}
          showAll={false}
          onRoutineSelected={onRoutineSelected}
        />
      </View>
      <RoutinesAssignedListModal
        isOpened={routinesModalOpened}
        onCompleteRoutines={onRoutineCompleted}
        onCancel={() => {
          setRoutinesModalOpened(false);
          setInitialRoutine(undefined);
        }}
        workspaceAsset={workspaceAsset!}
        initialRoutine={initialRoutine}
      />
    </>
  );
};

const styles = StyleSheet.create({
  containerHeader: {
    marginBottom: 10,
    paddingHorizontal: 16,
    paddingTop: 10,
  },
});
